import { useEffect, useState } from 'react';

const VERSION_URL = '/version.json';
const POLL_INTERVAL_MS = 5 * 60 * 1000;

async function fetchVersion(): Promise<string | null> {
  try {
    const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'omit',
    });
    if (!res.ok) return null;
    const data = await res.json();
    return typeof data?.version === 'string' ? data.version : null;
  } catch {
    return null;
  }
}

export function useVersionCheck(): { updateAvailable: boolean } {
  const [initialVersion, setInitialVersion] = useState<string | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    let cancelled = false;

    fetchVersion().then((v) => {
      if (!cancelled) setInitialVersion(v);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!initialVersion) return;

    let cancelled = false;

    const check = async () => {
      const latest = await fetchVersion();
      if (cancelled) return;
      if (latest && latest !== initialVersion) {
        setUpdateAvailable(true);
      }
    };

    const onFocus = () => check();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };

    const intervalId = window.setInterval(check, POLL_INTERVAL_MS);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [initialVersion]);

  return { updateAvailable };
}
